import { useEffect, useMemo, useRef } from "react";
import { useCompanyFilterOptions } from "../../hooks";
import {
  useCompanyFiltersStore,
  useCompanyFiltersStoreApi,
} from "../../provider/CompanyFilterProvider";
import type { TargetOrganizationsOptions } from "./options";
import type { CachedCompany } from "./slice";
import { getActiveIds } from "./utils";

/**
 * Hook that automatically hydrates the targetOrganizations cache when
 * IDs are present but company details are missing.
 *
 * This is called from the controller to ensure hydration happens
 * regardless of whether the filter panel is open.
 */
export function useTargetOrganizationsHydration(): void {
  const slice = useCompanyFiltersStore((s) => s.slices.targetOrganizations);
  const storeApi = useCompanyFiltersStoreApi();
  const options = useCompanyFilterOptions("targetOrganizations") as
    | TargetOrganizationsOptions
    | undefined;

  const activeIds = useMemo(() => getActiveIds(slice), [slice]);
  const cachedIds = useMemo(
    () => new Set(slice.companiesCache.map((c) => c.hgId)),
    [slice.companiesCache]
  );
  const missingIds = useMemo(
    () => activeIds.filter((id) => !cachedIds.has(id)),
    [activeIds, cachedIds]
  );

  // Track in-flight request to avoid duplicates and handle stale responses
  const requestRef = useRef<string | null>(null);

  // Note: We intentionally exclude `slice` and `cachedIds` from deps.
  // - `missingIds` already derives from them, so changes are captured
  // - We use `storeApi.getState()` at resolve time to read the LATEST state,
  //   avoiding state clobber if user changes selections during the request
  // - Including them would cause unnecessary effect re-runs
  useEffect(() => {
    if (missingIds.length === 0 || !options?.onHydrate) return;

    const requestKey = missingIds.join(",");

    // Skip if we're already fetching this exact set of IDs
    if (requestRef.current === requestKey) return;
    requestRef.current = requestKey;

    options
      .onHydrate(missingIds)
      .then((companies) => {
        // Ignore stale responses
        if (requestRef.current !== requestKey) return;

        // Read the LATEST slice to avoid clobbering concurrent user changes
        const latestSlice = storeApi.getState().slices.targetOrganizations;
        const latestCachedIds = new Set(
          latestSlice.companiesCache.map((c) => c.hgId)
        );

        // Merge new companies into cache, avoiding duplicates
        const newCompanies = companies.filter(
          (c: CachedCompany) => !latestCachedIds.has(c.hgId)
        );

        storeApi.getState().setSlice("targetOrganizations", {
          ...latestSlice,
          companiesCache: [...latestSlice.companiesCache, ...newCompanies],
        });
      })
      .catch(() => {
        // Silently handle errors - hydration will retry on next render
      })
      .finally(() => {
        // Always clear the request lock to allow retries
        if (requestRef.current === requestKey) {
          requestRef.current = null;
        }
      });
  }, [missingIds, options, storeApi]);
}
